import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  MESSAGE_POST_PAGE_LIMIT,
  fetchMessagePosts,
  fetchMessageThread,
  markMessagePostDelivered,
  markMessagePostSeen,
  messageReceiptLine,
  type MessagePost,
  type MessageThreadDetail,
} from "../../../../packages/shared/src/messages.ts";
import { MessageNotice } from "./MessageNotice.tsx";
import {
  RETENTION_COPY,
  formatMessageWhen,
  messageComposerPlaceholder,
  messageRoleLabel,
} from "./message-model.ts";
import { messageOutboxStatusLine, outboxForThread, type MessageOutboxItem } from "./message-outbox.ts";

export interface MessageThreadOutbox {
  readonly networkOnline: boolean;
  readonly outbox: readonly MessageOutboxItem[];
  readonly queuePost: (threadId: string, body: string) => void;
  readonly reconcile: (posts: readonly { readonly postId: string }[]) => void;
  readonly retryPost: (clientPostId: string) => void;
}

export function MessageThreadScreen({
  accountId,
  client,
  currentAuthorName,
  currentAuthorRole,
  officerId,
  onBack,
  onThreadRead,
  outbox,
  refreshVersion,
  threadId,
}: {
  readonly accountId: string;
  readonly client: SupabaseClient;
  readonly currentAuthorName: string;
  readonly currentAuthorRole: string;
  readonly officerId: string;
  readonly onBack: () => void;
  readonly onThreadRead: () => void;
  readonly outbox: MessageThreadOutbox;
  readonly refreshVersion: number;
  readonly threadId: string;
}) {
  const [detail, setDetail] = useState<MessageThreadDetail | null>(null);
  const [posts, setPosts] = useState<readonly MessagePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const requestGenerationRef = useRef(0);
  const reconcile = outbox.reconcile;

  const recordReceipts = useCallback(async (loaded: readonly MessagePost[]) => {
    const incoming = loaded.filter((post) => !post.isOwnPost);
    await Promise.all(incoming.map(async (post) => {
      await markMessagePostDelivered(client, accountId, post.postId);
      await markMessagePostSeen(client, accountId, post.postId);
    }));
  }, [accountId, client]);

  const refresh = useCallback(async () => {
    const generation = ++requestGenerationRef.current;
    setLoading(true);
    setLoadingOlder(false);
    setFailure(null);
    const [detailLoad, postsLoad] = await Promise.all([
      fetchMessageThread(client, accountId, threadId),
      fetchMessagePosts(client, accountId, threadId),
    ]);
    if (requestGenerationRef.current !== generation) return;
    if (detailLoad.status !== "ready") {
      setDetail(null);
      setFailure(detailLoad.reason);
      setLoading(false);
      return;
    }
    setDetail(detailLoad.value);
    if (postsLoad.status !== "ready") {
      setFailure(postsLoad.reason);
      setLoading(false);
      return;
    }
    const chronological = [...postsLoad.value].reverse();
    setPosts(chronological);
    setHasOlder(postsLoad.value.length === MESSAGE_POST_PAGE_LIMIT);
    setLoading(false);
    reconcile(chronological);
    onThreadRead();
    void recordReceipts(postsLoad.value);
  }, [accountId, client, onThreadRead, reconcile, recordReceipts, threadId]);

  useEffect(() => {
    requestGenerationRef.current += 1;
    setDetail(null);
    setPosts([]);
    setLoadingOlder(false);
    setHasOlder(false);
    setFailure(null);
    setDraft("");
  }, [accountId, officerId, threadId]);

  useEffect(() => {
    void refresh();
  }, [refresh, refreshVersion]);

  const loadOlder = useCallback(async () => {
    const oldest = posts[0];
    if (oldest === undefined || loadingOlder) return;
    const generation = requestGenerationRef.current;
    setLoadingOlder(true);
    const load = await fetchMessagePosts(client, accountId, threadId, oldest.postId);
    if (requestGenerationRef.current !== generation) return;
    if (load.status !== "ready") {
      setFailure(load.reason);
      setLoadingOlder(false);
      return;
    }
    const known = new Set(posts.map((post) => post.postId));
    const older = [...load.value].reverse().filter((post) => !known.has(post.postId));
    const merged = [...older, ...posts];
    setPosts(merged);
    setHasOlder(load.value.length === MESSAGE_POST_PAGE_LIMIT);
    setLoadingOlder(false);
    reconcile(merged);
    void recordReceipts(load.value);
  }, [accountId, client, loadingOlder, posts, reconcile, recordReceipts, threadId]);

  const pending = useMemo(
    () => outboxForThread(outbox.outbox, accountId, officerId, threadId),
    [accountId, officerId, outbox.outbox, threadId],
  );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = draft.trim();
    if (body === "" || detail === null || !detail.thread.callerCanSend) return;
    outbox.queuePost(threadId, body);
    setDraft("");
  };

  return (
    <section className="messages-page" aria-label="Message thread">
      <header className="messages-page__header">
        <button className="messages-back" type="button" onClick={onBack}>Back to Messages</button>
        <button className="messages-quiet-button" type="button" disabled={loading} onClick={() => void refresh()}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      {detail !== null ? (
        <div className="messages-thread-heading">
          <h2>{detail.thread.name}</h2>
          <p>{detail.thread.kind === "direct" ? "Direct" : `Crew · ${detail.thread.memberCount} members`}</p>
        </div>
      ) : null}

      {failure !== null ? <MessageNotice copy={failure} onRetry={() => void refresh()} /> : null}
      {loading && detail === null ? <p className="messages-state" role="status">Loading thread...</p> : null}

      {hasOlder ? (
        <button
          className="messages-load-older"
          type="button"
          disabled={loadingOlder}
          onClick={() => void loadOlder()}
        >
          {loadingOlder ? "Loading..." : "Load older"}
        </button>
      ) : null}

      <div className="messages-posts" aria-live="polite">
        {posts.length === 0 && !loading ? <p className="messages-state">No messages in this thread yet.</p> : null}
        {posts.map((post) => <MessagePostCard key={post.postId} post={post} />)}
        {pending.map((item) => (
          <OutboxPostCard
            authorName={currentAuthorName}
            authorRole={currentAuthorRole}
            item={item}
            key={item.clientPostId}
            networkOnline={outbox.networkOnline}
            onRetry={() => outbox.retryPost(item.clientPostId)}
          />
        ))}
      </div>

      {detail !== null && detail.thread.callerCanSend ? (
        <form className="messages-composer" onSubmit={submit}>
          <label htmlFor="message-draft">{messageComposerPlaceholder(detail.thread.kind, detail.thread.name)}</label>
          <textarea
            id="message-draft"
            maxLength={10000}
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <button type="submit" disabled={draft.trim() === ""}>Send</button>
        </form>
      ) : detail !== null ? (
        <p className="messages-read-only">Read only for this membership.</p>
      ) : null}
      {detail !== null ? <p className="messages-retention">{RETENTION_COPY}</p> : null}
    </section>
  );
}

function MessagePostCard({ post }: { readonly post: MessagePost }) {
  return (
    <article className={`messages-post${post.isOwnPost ? " messages-post--own" : ""}`}>
      <p className="messages-post__author">{post.authorDisplayName} · {messageRoleLabel(post.authorRole)}</p>
      <p className="messages-post__body">{post.body}</p>
      <p className="messages-post__receipt">
        {post.isOwnPost
          ? messageReceiptLine(post, formatMessageWhen)
          : formatMessageWhen(post.createdAt)}
      </p>
    </article>
  );
}

function OutboxPostCard({
  authorName,
  authorRole,
  item,
  networkOnline,
  onRetry,
}: {
  readonly authorName: string;
  readonly authorRole: string;
  readonly item: MessageOutboxItem;
  readonly networkOnline: boolean;
  readonly onRetry: () => void;
}) {
  const failed = item.status === "failed";
  return (
    <article className={`messages-post messages-post--own${failed ? " messages-post--failed" : ""}`}>
      <p className="messages-post__author">{authorName} · {messageRoleLabel(authorRole)}</p>
      <p className="messages-post__body">{item.body}</p>
      {failed ? (
        <button className="messages-post__retry" type="button" onClick={onRetry}>
          {messageOutboxStatusLine(item, formatMessageWhen)}
        </button>
      ) : (
        <p className="messages-post__receipt">{messageOutboxStatusLine(item, formatMessageWhen)}</p>
      )}
      {failed && !networkOnline ? <p className="messages-post__offline-hint">Retry will wait for connection.</p> : null}
    </article>
  );
}
